/**
 * Why V2 was created:
 * 
 * The original version of the DateRangePicker did not support editing the value with keyboard.
 * MUI 7 provides a DateRangePicker with keyboard editing and configurable validation straight out of the box.
 * In order to support incremental upgrade of the existing DateTimePicker components in the codebase, a separate version V2 was created alongside V1.
 */

import { useCallback, useMemo, useState, useEffect } from 'react'
import {
  DateRangePicker as MuiDateRangePicker
} from '@mui/x-date-pickers-pro-7/DateRangePicker'
import { AdapterDateFns } from '@mui/x-date-pickers-pro-7/AdapterDateFns'
import { Box } from '@mui/material'
import {
  DateRange,
  LocalizationProvider,
} from '@mui/x-date-pickers-pro-7'
import { dateFnsLocale, localeDateFormat } from '../../../utils/localeUtils'

import { useValidation, DateRangeValidationError } from './useValidation'
import {
  calculateDisableAfterDate,
  calculateDisableBeforeDate,
  shouldDisableDate
} from './utils'
import {
  DEFAULT_VALUES,
  DAY_SLOT_STYLES,
  FIELD_SEPARATOR_STYLES
} from './constants'

export type { DateRangeValidationError } from './useValidation'

type Props = {
  dataTestId?: string
  name?: string
  id?: string
  onChange: (date: DateRange<Date>, context?: object) => void
  onError?: (error: DateRangeValidationError) => void
  onOpen?: () => void
  value: DateRange<Date>
  arrange?: 'Horizontal' | 'Stack'
  required?: boolean
  helperText?: 'Required' | 'Optional'
  maxDate?: Date | undefined
  minDate?: Date | undefined
  /** Should be ISO format e.g. 2022-01-01 */
  disabledDates?: string[]
  startText?: string
  endText?: string
  disabled?: boolean
  disablePast?: boolean
  disableOnOrAfterMonths?: number
  closeOnSelect?: boolean
  calendars?: 1 | 2 | 3
  spacing?: 1 | 2 | 3
  weekendOnly?: boolean
  /** Maximum allowed date range in days */
  maxRangeDays?: number
  fromDateError?: boolean
  toDateError?: boolean
}

function DateRangePicker({
  dataTestId,
  onChange,
  onError,
  onOpen,
  value,
  arrange = DEFAULT_VALUES.arrange,
  required,
  helperText,
  maxDate,
  minDate,
  disabledDates,
  fromDateError,
  toDateError,
  startText = DEFAULT_VALUES.startText,
  endText = DEFAULT_VALUES.endText,
  disabled = DEFAULT_VALUES.disabled,
  disablePast = DEFAULT_VALUES.disablePast,
  disableOnOrAfterMonths,
  closeOnSelect = DEFAULT_VALUES.closeOnSelect,
  calendars = DEFAULT_VALUES.calendars,
  spacing = DEFAULT_VALUES.spacing,
  weekendOnly = DEFAULT_VALUES.weekendOnly,
  maxRangeDays = DEFAULT_VALUES.maxRangeDays,
  ...rest
}: Props) {
  const [touched, setTouched] = useState(false)

  const [fromDate, toDate] = value

  // Calculate disabled date ranges
  const disableAfterDate = useMemo(
    () => calculateDisableAfterDate(fromDate, disableOnOrAfterMonths),
    [disableOnOrAfterMonths, fromDate]
  )

  const disableBeforeDate = useMemo(
    () => calculateDisableBeforeDate(toDate, disableOnOrAfterMonths),
    [disableOnOrAfterMonths, toDate]
  )

  // Disable date logic
  const disableDate = useCallback((date: Date): boolean =>
    shouldDisableDate(date, {
      weekendOnly,
      disabledDates
    })
  , [disabledDates, weekendOnly])

  // Setup validation
  const { startFieldValidation, endFieldValidation, onError: handleInternalError } = useValidation({
    value,
    touched,
    required,
    helperText,
    maxDate,
    minDate,
    maxRangeDays,
    fromDateError,
    toDateError
  })

  // Report errors to parent
  useEffect(() => {
    if (onError) {
      onError([startFieldValidation.errorType, endFieldValidation.errorType])
    }
  }, [startFieldValidation.errorType, endFieldValidation.errorType, onError])

  // Generate text field props
  const getTextFieldProps = useCallback((params: any) => {
    const isStartField = params.position === 'start'
    const validation = isStartField ? startFieldValidation : endFieldValidation
    const fieldDataTestId = `${dataTestId}${isStartField ? 'Tab-DateFrom' : 'Tab-DateTo'}`
    
    return {
      fullWidth: true,
      autoComplete: 'off',
      inputProps: { 'data-testid': fieldDataTestId },
      error: validation.error,
      helperText: validation.helperText
    }
  }, [dataTestId, startFieldValidation, endFieldValidation])

  return (
    <Box width={arrange === 'Stack' ? 'unset' : '100%'}>
      <LocalizationProvider
        dateAdapter={AdapterDateFns}
        localeText={{ start: startText, end: endText }}
        adapterLocale={dateFnsLocale}
      >
        <MuiDateRangePicker
          enableAccessibleFieldDOMStructure
          shouldDisableDate={disableDate}
          format={localeDateFormat}
          value={value}
          disabled={disabled}
          calendars={calendars}
          onChange={(newValue) => {
            setTouched(true)
            onChange(newValue)
          }}
          onError={handleInternalError}
          maxDate={maxDate ?? disableAfterDate}
          minDate={minDate ?? disableBeforeDate}
          disablePast={disablePast}
          closeOnSelect={closeOnSelect}
          onOpen={onOpen}
          sx={{ alignItems: 'start' }}
          slotProps={{
            day: DAY_SLOT_STYLES,
            textField: getTextFieldProps,
            fieldSeparator: FIELD_SEPARATOR_STYLES
          }}
          {...rest}
        />
      </LocalizationProvider>
    </Box>
  )
}

export default DateRangePicker
